import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import { FileText, Scale, ShieldCheck, AlertTriangle, Package, CreditCard, XCircle, RefreshCw, Mail } from 'lucide-react';

const TermsPage = () => {
  const { currentLanguage } = useLanguage();

  const translations = {
    en: {
      badge: "Legal",
      title: 'Terms & Conditions',
      lastUpdated: 'Last Updated',
      intro: 'Welcome to PenLab Peptide. By accessing and using our website and services, you agree to be bound by these Terms and Conditions. Please read them carefully before placing any order.',
      sections: [
        {
          icon: Scale,
          title: '1. Acceptance of Terms',
          content: `By accessing this website, you confirm that you are a licensed professional, researcher, or authorized representative of a licensed institution. You agree to comply with all applicable laws and regulations regarding the purchase and use of research compounds. If you do not agree with these terms, you must not use our services.`
        },
        {
          icon: ShieldCheck,
          title: '2. Eligibility & Verification',
          content: `Our products are sold exclusively to verified professionals and institutions for legitimate research purposes. We reserve the right to:
          
• Request verification documents including professional licenses, institutional affiliation, and identification
• Reject or cancel orders from unverified accounts
• Terminate accounts that provide false or misleading information
• Report suspicious activity to relevant authorities

All account applications are subject to our verification process, which typically takes 24-48 hours.`
        },
        {
          icon: AlertTriangle,
          title: '3. Product Use Disclaimer',
          content: `IMPORTANT: All products sold by PenLab Peptide are intended for RESEARCH PURPOSES ONLY. Our products are:

• NOT intended for human or animal consumption
• NOT intended for diagnostic, therapeutic, or medicinal use
• NOT approved by any regulatory agency for clinical use
• Sold strictly for in-vitro research and laboratory use

By purchasing our products, you acknowledge and agree that you will use them solely for legitimate scientific research in accordance with all applicable laws and regulations.`
        },
        {
          icon: Package,
          title: '4. Orders & Shipping',
          content: `Order Processing:
• All orders are subject to verification and acceptance
• We reserve the right to refuse or cancel any order at our discretion
• Order confirmations do not constitute acceptance until verification is complete

Shipping:
• We ship via DHL Express within 3-5 business days to most European destinations
• Shipping times may vary based on destination and customs processing
• Risk of loss transfers to you upon delivery to the carrier
• We are not responsible for delays caused by customs or regulatory inspections

You are responsible for ensuring compliance with import regulations in your jurisdiction.`
        },
        {
          icon: CreditCard,
          title: '5. Payment Terms',
          content: `Payment Methods:
• We accept bank transfer payments only
• All orders require completion of our KYC (Know Your Customer) verification process
• Payment must be received before order processing begins

Pricing:
• All prices are displayed in Euros (€) and include applicable taxes
• We reserve the right to modify prices without prior notice
• Promotional offers may be subject to additional terms and conditions`
        },
        {
          icon: XCircle,
          title: '6. Returns & Refunds',
          content: `Due to the nature of our products and regulatory requirements:

• Returns are only accepted for damaged or incorrect items
• Return requests must be submitted within 7 days of delivery
• Products must be unopened and in original packaging
• Refunds will be processed within 14 business days of receiving returned items

We do not accept returns for:
• Change of mind or incorrect ordering
• Products that have been opened or tampered with
• Orders rejected by customs in your country`
        },
        {
          icon: RefreshCw,
          title: '7. Limitation of Liability',
          content: `To the maximum extent permitted by law:

• PenLab Peptide shall not be liable for any indirect, incidental, or consequential damages
• Our total liability shall not exceed the amount paid for the specific product in question
• We are not liable for any misuse of products or non-compliance with research protocols
• We make no warranties regarding fitness for any particular purpose

You agree to indemnify and hold PenLab Peptide harmless from any claims arising from your use of our products or violation of these terms.`
        }
      ],
      contact: 'Contact Us',
      contactText: 'If you have any questions about these Terms & Conditions, please contact us at:',
      email: 'info@penlabpeptide.com'
    },
    es: {
      badge: "Legal",
      title: 'Términos y Condiciones',
      lastUpdated: 'Última Actualización',
      intro: 'Bienvenido a PenLab Peptide. Al acceder y utilizar nuestro sitio web y servicios, usted acepta estar sujeto a estos Términos y Condiciones. Por favor, léalos detenidamente antes de realizar cualquier pedido.',
      sections: [
        {
          icon: Scale,
          title: '1. Aceptación de los Términos',
          content: `Al acceder a este sitio web, usted confirma que es un profesional autorizado, investigador o representante autorizado de una institución autorizada. Usted acepta cumplir con todas las leyes y regulaciones aplicables con respecto a la compra y uso de compuestos de investigación. Si no está de acuerdo con estos términos, no debe utilizar nuestros servicios.`
        },
        {
          icon: ShieldCheck,
          title: '2. Elegibilidad y Verificación',
          content: `Nuestros productos se venden exclusivamente a profesionales e instituciones verificadas para fines de investigación legítimos. Nos reservamos el derecho de:
          
• Solicitar documentos de verificación incluyendo licencias profesionales, afiliación institucional e identificación
• Rechazar o cancelar pedidos de cuentas no verificadas
• Terminar cuentas que proporcionen información falsa o engañosa
• Reportar actividad sospechosa a las autoridades pertinentes

Todas las solicitudes de cuenta están sujetas a nuestro proceso de verificación, que típicamente toma 24-48 horas.`
        },
        {
          icon: AlertTriangle,
          title: '3. Aviso sobre el Uso del Producto',
          content: `IMPORTANTE: Todos los productos vendidos por PenLab Peptide están destinados ÚNICAMENTE PARA FINES DE INVESTIGACIÓN. Nuestros productos:

• NO están destinados para consumo humano o animal
• NO están destinados para uso diagnóstico, terapéutico o medicinal
• NO están aprobados por ninguna agencia reguladora para uso clínico
• Se venden estrictamente para investigación in-vitro y uso de laboratorio

Al comprar nuestros productos, usted reconoce y acepta que los utilizará únicamente para investigación científica legítima de acuerdo con todas las leyes y regulaciones aplicables.`
        },
        {
          icon: Package,
          title: '4. Pedidos y Envíos',
          content: `Procesamiento de Pedidos:
• Todos los pedidos están sujetos a verificación y aceptación
• Nos reservamos el derecho de rechazar o cancelar cualquier pedido a nuestra discreción
• Las confirmaciones de pedido no constituyen aceptación hasta que se complete la verificación

Envío:
• Enviamos a través de DHL Express en 3-5 días hábiles a la mayoría de destinos europeos
• Los tiempos de envío pueden variar según el destino y el procesamiento aduanero
• El riesgo de pérdida se transfiere a usted tras la entrega al transportista
• No somos responsables de retrasos causados por aduanas o inspecciones regulatorias

Usted es responsable de garantizar el cumplimiento de las regulaciones de importación en su jurisdicción.`
        },
        {
          icon: CreditCard,
          title: '5. Términos de Pago',
          content: `Métodos de Pago:
• Aceptamos únicamente pagos por transferencia bancaria
• Todos los pedidos requieren completar nuestro proceso de verificación KYC (Conozca a su Cliente)
• El pago debe recibirse antes de que comience el procesamiento del pedido

Precios:
• Todos los precios se muestran en Euros (€) e incluyen impuestos aplicables
• Nos reservamos el derecho de modificar los precios sin previo aviso
• Las ofertas promocionales pueden estar sujetas a términos y condiciones adicionales`
        },
        {
          icon: XCircle,
          title: '6. Devoluciones y Reembolsos',
          content: `Debido a la naturaleza de nuestros productos y los requisitos regulatorios:

• Las devoluciones solo se aceptan para artículos dañados o incorrectos
• Las solicitudes de devolución deben presentarse dentro de los 7 días posteriores a la entrega
• Los productos deben estar sin abrir y en su embalaje original
• Los reembolsos se procesarán dentro de los 14 días hábiles posteriores a la recepción de los artículos devueltos

No aceptamos devoluciones por:
• Cambio de opinión o pedido incorrecto
• Productos que han sido abiertos o manipulados
• Pedidos rechazados por aduanas en su país`
        },
        {
          icon: RefreshCw,
          title: '7. Limitación de Responsabilidad',
          content: `En la máxima medida permitida por la ley:

• PenLab Peptide no será responsable de ningún daño indirecto, incidental o consecuente
• Nuestra responsabilidad total no excederá el monto pagado por el producto específico en cuestión
• No somos responsables de ningún mal uso de los productos o incumplimiento de los protocolos de investigación
• No hacemos garantías con respecto a la idoneidad para ningún propósito particular

Usted acepta indemnizar y mantener indemne a PenLab Peptide de cualquier reclamación derivada de su uso de nuestros productos o violación de estos términos.`
        }
      ],
      contact: 'Contáctenos',
      contactText: 'Si tiene alguna pregunta sobre estos Términos y Condiciones, por favor contáctenos en:',
      email: 'info@penlabpeptide.com'
    },
    ru: {
      badge: "Правовая информация",
      title: 'Условия и Положения',
      lastUpdated: 'Последнее Обновление',
      intro: 'Добро пожаловать в PenLab Peptide. Получая доступ к нашему веб-сайту и услугам и используя их, вы соглашаетесь соблюдать настоящие Условия и Положения. Пожалуйста, внимательно прочитайте их перед оформлением заказа.',
      sections: [
        {
          icon: Scale,
          title: '1. Принятие Условий',
          content: `Получая доступ к этому веб-сайту, вы подтверждаете, что являетесь лицензированным специалистом, исследователем или уполномоченным представителем лицензированного учреждения. Вы соглашаетесь соблюдать все применимые законы и правила, касающиеся покупки и использования исследовательских соединений. Если вы не согласны с этими условиями, вы не должны использовать наши услуги.`
        },
        {
          icon: ShieldCheck,
          title: '2. Право на Участие и Верификация',
          content: `Наша продукция продается исключительно верифицированным специалистам и учреждениям для законных исследовательских целей. Мы оставляем за собой право:
          
• Запрашивать документы для верификации, включая профессиональные лицензии, институциональную принадлежность и удостоверение личности
• Отклонять или отменять заказы от неверифицированных аккаунтов
• Прекращать действие аккаунтов, предоставляющих ложную или вводящую в заблуждение информацию
• Сообщать о подозрительной деятельности соответствующим органам

Все заявки на создание аккаунта подлежат нашему процессу верификации, который обычно занимает 24-48 часов.`
        },
        {
          icon: AlertTriangle,
          title: '3. Отказ от Ответственности за Использование Продукта',
          content: `ВАЖНО: Вся продукция, продаваемая PenLab Peptide, предназначена ТОЛЬКО ДЛЯ ИССЛЕДОВАТЕЛЬСКИХ ЦЕЛЕЙ. Наша продукция:

• НЕ предназначена для употребления людьми или животными
• НЕ предназначена для диагностического, терапевтического или медицинского использования
• НЕ одобрена каким-либо регулирующим органом для клинического использования
• Продается исключительно для исследований in-vitro и лабораторного использования

Приобретая нашу продукцию, вы признаете и соглашаетесь, что будете использовать её исключительно для законных научных исследований в соответствии со всеми применимыми законами и правилами.`
        },
        {
          icon: Package,
          title: '4. Заказы и Доставка',
          content: `Обработка Заказов:
• Все заказы подлежат верификации и принятию
• Мы оставляем за собой право отказать или отменить любой заказ по нашему усмотрению
• Подтверждения заказа не являются принятием до завершения верификации

Доставка:
• Мы отправляем через DHL Express в течение 3-5 рабочих дней в большинство европейских направлений
• Сроки доставки могут варьироваться в зависимости от пункта назначения и таможенной обработки
• Риск утраты переходит к вам при передаче перевозчику
• Мы не несем ответственности за задержки, вызванные таможней или регулятивными проверками

Вы несете ответственность за соблюдение правил импорта в вашей юрисдикции.`
        },
        {
          icon: CreditCard,
          title: '5. Условия Оплаты',
          content: `Способы Оплаты:
• Мы принимаем только банковские переводы
• Все заказы требуют прохождения нашей процедуры KYC (Знай своего клиента)
• Оплата должна быть получена до начала обработки заказа

Цены:
• Все цены указаны в евро (€) и включают применимые налоги
• Мы оставляем за собой право изменять цены без предварительного уведомления
• Рекламные предложения могут быть предметом дополнительных условий`
        },
        {
          icon: XCircle,
          title: '6. Возвраты и Возмещения',
          content: `В связи с характером нашей продукции и нормативными требованиями:

• Возвраты принимаются только для поврежденных или неправильных товаров
• Заявки на возврат должны быть поданы в течение 7 дней после доставки
• Продукция должна быть невскрытой и в оригинальной упаковке
• Возмещения будут обработаны в течение 14 рабочих дней после получения возвращенных товаров

Мы не принимаем возвраты по причине:
• Изменения решения или неправильного заказа
• Продукции, которая была вскрыта или подвергалась вмешательству
• Заказов, отклоненных таможней в вашей стране`
        },
        {
          icon: RefreshCw,
          title: '7. Ограничение Ответственности',
          content: `В максимальной степени, разрешенной законом:

• PenLab Peptide не несет ответственности за любые косвенные, случайные или последующие убытки
• Наша общая ответственность не превышает сумму, уплаченную за конкретный продукт
• Мы не несем ответственности за любое неправильное использование продукции или несоблюдение протоколов исследований
• Мы не даем никаких гарантий относительно пригодности для какой-либо конкретной цели

Вы соглашаетесь возместить ущерб и освободить PenLab Peptide от любых претензий, возникающих в результате использования вами нашей продукции или нарушения настоящих условий.`
        }
      ],
      contact: 'Свяжитесь с Нами',
      contactText: 'Если у вас есть вопросы по настоящим Условиям и Положениям, пожалуйста, свяжитесь с нами:',
      email: 'info@penlabpeptide.com'
    },
    de: {
      badge: "Rechtlich",
      title: 'Allgemeine Geschäftsbedingungen',
      lastUpdated: 'Zuletzt aktualisiert',
      intro: 'Willkommen bei PenLab Peptide. Durch den Zugriff auf unsere Website und die Nutzung unserer Dienstleistungen erklären Sie sich mit diesen Allgemeinen Geschäftsbedingungen einverstanden. Bitte lesen Sie sie sorgfältig durch, bevor Sie eine Bestellung aufgeben.',
      sections: [
        {
          icon: Scale,
          title: '1. Annahme der Bedingungen',
          content: `Durch den Zugriff auf diese Website bestätigen Sie, dass Sie ein lizenzierter Fachmann, Forscher oder ein bevollmächtigter Vertreter einer lizenzierten Institution sind. Sie erklären sich damit einverstanden, alle geltenden Gesetze und Vorschriften in Bezug auf den Kauf und die Verwendung von Forschungsstoffen einzuhalten. Wenn Sie mit diesen Bedingungen nicht einverstanden sind, dürfen Sie unsere Dienstleistungen nicht nutzen.`
        },
        {
          icon: ShieldCheck,
          title: '2. Berechtigung & Verifizierung',
          content: `Unsere Produkte werden ausschließlich an verifizierte Fachleute und Institutionen für legitime Forschungszwecke verkauft. Wir behalten uns das Recht vor:
          
• Verifizierungsdokumente wie Berufslizenzen, institutionelle Zugehörigkeit und Identitätsnachweise anzufordern
• Bestellungen von nicht verifizierten Konten abzulehnen oder zu stornieren
• Konten zu kündigen, die falsche oder irreführende Angaben machen
• Verdächtige Aktivitäten den zuständigen Behörden zu melden

Alle Kontoanträge unterliegen unserem Verifizierungsprozess, der in der Regel 24-48 Stunden dauert.`
        },
        {
          icon: AlertTriangle,
          title: '3. Haftungsausschluss zur Produktverwendung',
          content: `WICHTIG: Alle von PenLab Peptide verkauften Produkte sind ausschließlich für FORSCHUNGSZWECKE BESTIMMT. Unsere Produkte sind:

• NICHT für den menschlichen oder tierischen Verzehr bestimmt
• NICHT für diagnostische, therapeutische oder medizinische Zwecke bestimmt
• Von keiner Aufsichtsbehörde für die klinische Anwendung zugelassen
• Strikt für in-vitro-Forschung und Laboranwendungen bestimmt

Durch den Kauf unserer Produkte bestätigen und akzeptieren Sie, dass Sie diese ausschließlich für legitime wissenschaftliche Forschung in Übereinstimmung mit allen geltenden Gesetzen und Vorschriften verwenden werden.`
        },
        {
          icon: Package,
          title: '4. Bestellungen & Versand',
          content: `Bestellabwicklung:
• Alle Bestellungen unterliegen der Verifizierung und Annahme
• Wir behalten uns das Recht vor, nach eigenem Ermessen Bestellungen abzulehnen oder zu stornieren
• Bestellbestätigungen stellen keine Annahme dar, solange die Verifizierung nicht abgeschlossen ist

Versand:
• Wir versenden mit DHL Express innerhalb von 3-5 Werktagen zu den meisten europäischen Zielen
• Die Lieferzeiten können je nach Bestimmungsort und Zollabwicklung variieren
• Das Verlustrisiko geht mit der Übergabe an den Frachtführer auf Sie über
• Wir sind nicht verantwortlich für Verzögerungen durch Zoll- oder behördliche Kontrollen

Sie sind dafür verantwortlich, die Einhaltung der Einfuhrbestimmungen in Ihrer Rechtsordnung sicherzustellen.`
        },
        {
          icon: CreditCard,
          title: '5. Zahlungsbedingungen',
          content: `Zahlungsmethoden:
• Wir akzeptieren ausschließlich Zahlungen per Banküberweisung
• Für alle Bestellungen ist die Durchführung unseres KYC-(Know Your Customer)-Verifizierungsprozesses erforderlich
• Die Zahlung muss eingehen, bevor mit der Bestellabwicklung begonnen wird

Preise:
• Alle Preise werden in Euro (€) angezeigt und enthalten die anfallenden Steuern
• Wir behalten uns das Recht vor, Preise ohne vorherige Ankündigung zu ändern
• Aktionsangebote können zusätzlichen Geschäftsbedingungen unterliegen`
        },
        {
          icon: XCircle,
          title: '6. Rückgaben & Rückerstattungen',
          content: `Aufgrund der Beschaffenheit unserer Produkte und der regulatorischen Anforderungen gilt:

• Rückgaben werden nur für beschädigte oder falsche Artikel akzeptiert
• Rückgabeanträge müssen innerhalb von 7 Tagen nach Lieferung gestellt werden
• Produkte müssen ungeöffnet und in der Originalverpackung sein
• Rückerstattungen werden innerhalb von 14 Werktagen nach Erhalt der zurückgesandten Artikel bearbeitet

Wir akzeptieren keine Rückgaben bei:
• Meinungsänderung oder falscher Bestellung
• Produkten, die geöffnet oder manipuliert wurden
• Bestellungen, die von den Zollbehörden in Ihrem Land abgelehnt wurden`
        },
        {
          icon: RefreshCw,
          title: '7. Haftungsbeschränkung',
          content: `Im maximal zulässigen gesetzlich geregelten Umfang gilt:

• PenLab Peptide haftet nicht für indirekte, beiläufig entstandene oder Folgeschäden
• Unsere Gesamthaftung übersteigt nicht den für das betreffende spezifische Produkt gezahlten Betrag
• Wir haften nicht für unsachgemäße Verwendung der Produkte oder Nichteinhaltung von Forschungsprotokollen
• Wir übernehmen keine Gewährleistung hinsichtlich der Eignung für einen bestimmten Zweck

Sie erklären sich damit einverstanden, PenLab Peptide von sämtlichen Ansprüchen freizustellen und schadlos zu halten, die sich aus Ihrer Nutzung unserer Produkte oder der Verletzung dieser Bedingungen ergeben.`
        }
      ],
      contact: 'Kontakt',
      contactText: 'Wenn Sie Fragen zu diesen Allgemeinen Geschäftsbedingungen haben, kontaktieren Sie uns bitte unter:',
      email: 'info@penlabpeptide.com'
    },
    fr: {
      badge: "Légal",
      title: 'Conditions générales',
      lastUpdated: 'Dernière mise à jour',
      intro: 'Bienvenue chez PenLab Peptide. En accédant à notre site Web et en utilisant nos services, vous acceptez d’être lié par les présentes Conditions générales. Veuillez les lire attentivement avant de passer toute commande.',
      sections: [
        {
          icon: Scale,
          title: '1. Acceptation des conditions',
          content: `En accédant à ce site Web, vous confirmez que vous êtes un professionnel autorisé, un chercheur, ou un représentant habilité d’un établissement agréé. Vous acceptez de respecter toutes les lois et réglementations applicables concernant l’achat et l’utilisation de composés de recherche. Si vous n’acceptez pas ces conditions, vous ne devez pas utiliser nos services.`
        },
        {
          icon: ShieldCheck,
          title: '2. Admissibilité et vérification',
          content: `Nos produits sont vendus exclusivement à des professionnels et des établissements vérifiés à des fins de recherche légitimes. Nous nous réservons le droit de :
          
• Demander des documents de vérification, y compris des licences professionnelles, une affiliation institutionnelle et une pièce d’identité
• Refuser ou annuler des commandes provenant de comptes non vérifiés
• Résilier les comptes fournissant des informations fausses ou trompeuses
• Signaler toute activité suspecte aux autorités compétentes

Toutes les demandes de création de compte sont soumises à notre processus de vérification, qui prend généralement 24 à 48 heures.`
        },
        {
          icon: AlertTriangle,
          title: '3. Avertissement concernant l’utilisation des produits',
          content: `IMPORTANT : Tous les produits vendus par PenLab Peptide sont destinés UNIQUEMENT À DES FINS DE RECHERCHE. Nos produits ne sont :

• PAS destinés à la consommation humaine ou animale
• PAS destinés à un usage diagnostique, thérapeutique ou médical
• PAS approuvés par une quelconque autorité de régulation pour un usage clinique
• Vendus strictement pour la recherche in vitro et l’utilisation en laboratoire

En achetant nos produits, vous reconnaissez et acceptez que vous les utiliserez exclusivement pour une recherche scientifique légitime, conformément à toutes les lois et réglementations applicables.`
        },
        {
          icon: Package,
          title: '4. Commandes et expédition',
          content: `Traitement des commandes :
• Toutes les commandes sont soumises à vérification et acceptation
• Nous nous réservons le droit de refuser ou d’annuler toute commande à notre discrétion
• Les confirmations de commande ne constituent pas une acceptation tant que la vérification n’est pas terminée

Expédition :
• Nous expédions via DHL Express dans un délai de 3 à 5 jours ouvrables vers la plupart des destinations européennes
• Les délais d’expédition peuvent varier en fonction de la destination et du traitement douanier
• Le risque de perte vous est transféré dès la remise au transporteur
• Nous ne sommes pas responsables des retards causés par les services douaniers ou les contrôles réglementaires

Il vous incombe de garantir le respect des réglementations d’importation en vigueur dans votre juridiction.`
        },
        {
          icon: CreditCard,
          title: '5. Conditions de paiement',
          content: `Moyens de paiement :
• Nous n’acceptons que les paiements par virement bancaire
• Toutes les commandes nécessitent l’achèvement de notre procédure KYC (Know Your Customer)
• Le paiement doit être reçu avant le début du traitement de la commande

Tarification :
• Tous les prix sont affichés en euros (€) et incluent les taxes applicables
• Nous nous réservons le droit de modifier les prix sans préavis
• Les offres promotionnelles peuvent être soumises à des conditions générales supplémentaires`
        },
        {
          icon: XCircle,
          title: '6. Retours et remboursements',
          content: `En raison de la nature de nos produits et des exigences réglementaires :

• Les retours ne sont acceptés que pour les articles endommagés ou incorrects
• Les demandes de retour doivent être soumises dans les 7 jours suivant la livraison
• Les produits doivent être non ouverts et dans leur emballage d’origine
• Les remboursements seront traités dans un délai de 14 jours ouvrables à compter de la réception des articles retournés

Nous n’acceptons pas de retours pour :
• Changement d’avis ou erreur de commande
• Produits qui ont été ouverts ou altérés
• Commandes rejetées par les douanes de votre pays`
        },
        {
          icon: RefreshCw,
          title: '7. Limitation de responsabilité',
          content: `Dans toute la mesure permise par la loi :

• PenLab Peptide ne pourra être tenue responsable d’aucun dommage indirect, accessoire ou consécutif
• Notre responsabilité totale ne dépassera pas le montant payé pour le produit spécifique concerné
• Nous ne sommes pas responsables d’une utilisation inappropriée des produits ou du non-respect des protocoles de recherche
• Nous ne donnons aucune garantie quant à l’aptitude à un usage particulier

Vous acceptez d’indemniser PenLab Peptide et de la dégager de toute responsabilité à l’égard de toute réclamation résultant de votre utilisation de nos produits ou de la violation des présentes conditions.`
        }
      ],
      contact: 'Contactez-nous',
      contactText: 'Si vous avez des questions concernant ces Conditions générales, veuillez nous contacter à l’adresse suivante :',
      email: 'info@penlabpeptide.com'
    },
    it: {
      badge: "Legale",
      title: 'Termini e Condizioni',
      lastUpdated: 'Ultimo aggiornamento',
      intro: 'Benvenuto su PenLab Peptide. Accedendo e utilizzando il nostro sito web e i nostri servizi, accetti di essere vincolato dai presenti Termini e Condizioni. Ti preghiamo di leggerli attentamente prima di effettuare qualsiasi ordine.',
      sections: [
        {
          icon: Scale,
          title: '1. Accettazione dei Termini',
          content: `Accedendo a questo sito web, confermi di essere un professionista autorizzato, un ricercatore o un rappresentante autorizzato di un istituto autorizzato. Accetti di rispettare tutte le leggi e i regolamenti applicabili relativi all'acquisto e all'uso di composti di ricerca. Se non sei d'accordo con questi termini, non devi utilizzare i nostri servizi.`
        },
        {
          icon: ShieldCheck,
          title: '2. Idoneità e Verifica',
          content: `I nostri prodotti sono venduti esclusivamente a professionisti e istituzioni verificati per finalità di ricerca legittime. Ci riserviamo il diritto di:
          
• Richiedere documenti di verifica, inclusi licenze professionali, affiliazione istituzionale e identificazione
• Rifiutare o annullare ordini provenienti da account non verificati
• Chiudere account che forniscono informazioni false o fuorvianti
• Segnalare attività sospette alle autorità competenti

Tutte le richieste di apertura account sono soggette al nostro processo di verifica, che in genere richiede 24-48 ore.`
        },
        {
          icon: AlertTriangle,
          title: '3. Dichiarazione di non responsabilità sull\'uso dei prodotti',
          content: `IMPORTANTE: Tutti i prodotti venduti da PenLab Peptide sono destinati ESCLUSIVAMENTE A SCOPI DI RICERCA. I nostri prodotti:

• NON sono destinati al consumo umano o animale
• NON sono destinati a uso diagnostico, terapeutico o medicinale
• NON sono approvati da alcuna autorità regolatoria per uso clinico
• Sono venduti esclusivamente per ricerca in vitro e uso di laboratorio

Acquistando i nostri prodotti, riconosci e accetti di utilizzarli esclusivamente per ricerca scientifica legittima in conformità con tutte le leggi e i regolamenti applicabili.`
        },
        {
          icon: Package,
          title: '4. Ordini e Spedizioni',
          content: `Elaborazione degli ordini:
• Tutti gli ordini sono soggetti a verifica e accettazione
• Ci riserviamo il diritto di rifiutare o annullare qualsiasi ordine a nostra discrezione
• Le conferme d'ordine non costituiscono accettazione finché la verifica non è completata

Spedizione:
• Spediamo tramite DHL Express entro 3-5 giorni lavorativi verso la maggior parte delle destinazioni europee
• I tempi di spedizione possono variare in base alla destinazione e alle procedure doganali
• Il rischio di perdita si trasferisce a te al momento della consegna al vettore
• Non siamo responsabili per ritardi causati da controlli doganali o ispezioni regolatorie

È tua responsabilità garantire il rispetto delle normative di importazione nella tua giurisdizione.`
        },
        {
          icon: CreditCard,
          title: '5. Termini di Pagamento',
          content: `Metodi di pagamento:
• Accettiamo pagamenti esclusivamente tramite bonifico bancario
• Tutti gli ordini richiedono il completamento del nostro processo di verifica KYC (Know Your Customer)
• Il pagamento deve essere ricevuto prima dell'inizio dell'elaborazione dell'ordine

Prezzi:
• Tutti i prezzi sono indicati in Euro (€) e includono le tasse applicabili
• Ci riserviamo il diritto di modificare i prezzi senza preavviso
• Le offerte promozionali possono essere soggette a termini e condizioni aggiuntivi`
        },
        {
          icon: XCircle,
          title: '6. Resi e Rimborsi',
          content: `A causa della natura dei nostri prodotti e dei requisiti normativi:

• I resi sono accettati solo per articoli danneggiati o errati
• Le richieste di reso devono essere inoltrate entro 7 giorni dalla consegna
• I prodotti devono essere integri e nella confezione originale
• I rimborsi saranno elaborati entro 14 giorni lavorativi dal ricevimento degli articoli restituiti

Non accettiamo resi per:
• Ripensamento o ordine errato
• Prodotti che sono stati aperti o manomessi
• Ordini rifiutati dalla dogana nel tuo Paese`
        },
        {
          icon: RefreshCw,
          title: '7. Limitazione di Responsabilità',
          content: `Nella massima misura consentita dalla legge:

• PenLab Peptide non sarà responsabile per danni indiretti, incidentali o consequenziali
• La nostra responsabilità totale non supererà l'importo pagato per lo specifico prodotto in questione
• Non siamo responsabili per uso improprio dei prodotti o mancato rispetto dei protocolli di ricerca
• Non forniamo alcuna garanzia di idoneità a uno scopo particolare

Accetti di manlevare e tenere indenne PenLab Peptide da qualsiasi reclamo derivante dal tuo uso dei nostri prodotti o dalla violazione dei presenti termini.`
        }
      ],
      contact: 'Contattaci',
      contactText: 'Se hai domande in merito ai presenti Termini e Condizioni, contattaci a:',
      email: 'info@penlabpeptide.com'
    },
    pl: {
      badge: "Prawne",
      title: 'Regulamin',
      lastUpdated: 'Ostatnia aktualizacja',
      intro: 'Witamy w PenLab Peptide. Uzyskując dostęp do naszej strony internetowej i korzystając z naszych usług, akceptujesz niniejszy Regulamin. Prosimy o dokładne zapoznanie się z nim przed złożeniem jakiegokolwiek zamówienia.',
      sections: [
        {
          icon: Scale,
          title: '1. Akceptacja warunków',
          content: `Uzyskując dostęp do tej strony internetowej, potwierdzasz, że jesteś licencjonowanym profesjonalistą, badaczem lub upoważnionym przedstawicielem licencjonowanej instytucji. Zobowiązujesz się do przestrzegania wszystkich obowiązujących przepisów prawa i regulacji dotyczących zakupu i stosowania związków do badań. Jeśli nie zgadzasz się z tymi warunkami, nie możesz korzystać z naszych usług.`
        },
        {
          icon: ShieldCheck,
          title: '2. Kwalifikowalność i weryfikacja',
          content: `Nasze produkty są sprzedawane wyłącznie zweryfikowanym specjalistom i instytucjom w uzasadnionych celach badawczych. Zastrzegamy sobie prawo do:
          
• Żądania dokumentów weryfikacyjnych, w tym licencji zawodowych, potwierdzenia powiązania instytucjonalnego oraz dokumentu tożsamości
• Odrzucenia lub anulowania zamówień z niezweryfikowanych kont
• Zamykania kont, które podają fałszywe lub wprowadzające w błąd informacje
• Zgłaszania podejrzanych działań odpowiednim organom

Wszystkie wnioski o założenie konta podlegają naszemu procesowi weryfikacji, który zazwyczaj trwa 24-48 godzin.`
        },
        {
          icon: AlertTriangle,
          title: '3. Zastrzeżenie dotyczące stosowania produktów',
          content: `WAŻNE: Wszystkie produkty sprzedawane przez PenLab Peptide są przeznaczone WYŁĄCZNIE DO CELÓW BADAWCZYCH. Nasze produkty:

• NIE są przeznaczone do spożycia przez ludzi ani zwierzęta
• NIE są przeznaczone do zastosowań diagnostycznych, terapeutycznych ani medycznych
• NIE są zatwierdzone przez żaden organ regulacyjny do zastosowań klinicznych
• Są sprzedawane wyłącznie do badań in vitro i zastosowań laboratoryjnych

Kupując nasze produkty, potwierdzasz i zgadzasz się, że będziesz je wykorzystywać wyłącznie do legalnych badań naukowych, zgodnie ze wszystkimi obowiązującymi przepisami prawa i regulacjami.`
        },
        {
          icon: Package,
          title: '4. Zamówienia i wysyłka',
          content: `Realizacja zamówień:
• Wszystkie zamówienia podlegają weryfikacji i akceptacji
• Zastrzegamy sobie prawo do odmowy realizacji lub anulowania dowolnego zamówienia według naszego uznania
• Potwierdzenie złożenia zamówienia nie oznacza jego akceptacji do czasu zakończenia weryfikacji

Wysyłka:
• Wysyłamy za pośrednictwem DHL Express w ciągu 3-5 dni roboczych do większości europejskich miejsc docelowych
• Czas dostawy może się różnić w zależności od miejsca docelowego i procedur celnych
• Ryzyko utraty przechodzi na Ciebie w momencie przekazania przesyłki przewoźnikowi
• Nie ponosimy odpowiedzialności za opóźnienia spowodowane przez odprawę celną lub kontrole regulacyjne

Jesteś odpowiedzialny za zapewnienie zgodności z przepisami importowymi w Twojej jurysdykcji.`
        },
        {
          icon: CreditCard,
          title: '5. Warunki płatności',
          content: `Metody płatności:
• Akceptujemy wyłącznie płatności przelewem bankowym
• Wszystkie zamówienia wymagają ukończenia naszego procesu weryfikacji KYC (Know Your Customer)
• Płatność musi zostać zaksięgowana przed rozpoczęciem realizacji zamówienia

Ceny:
• Wszystkie ceny są podawane w euro (€) i zawierają obowiązujące podatki
• Zastrzegamy sobie prawo do zmiany cen bez uprzedzenia
• Oferty promocyjne mogą podlegać dodatkowym warunkom`
        },
        {
          icon: XCircle,
          title: '6. Zwroty i refundacje',
          content: `Ze względu na charakter naszych produktów oraz wymagania regulacyjne:

• Zwroty są akceptowane wyłącznie w przypadku uszkodzonych lub błędnie dostarczonych produktów
• Zgłoszenia zwrotu muszą zostać złożone w ciągu 7 dni od dostawy
• Produkty muszą być nieotwarte i w oryginalnym opakowaniu
• Zwroty płatności zostaną zrealizowane w ciągu 14 dni roboczych od otrzymania zwróconych produktów

Nie przyjmujemy zwrotów z tytułu:
• Zmiany decyzji lub błędnie złożonego zamówienia
• Produktów, które zostały otwarte lub naruszone
• Zamówień odrzuconych przez organy celne w Twoim kraju`
        },
        {
          icon: RefreshCw,
          title: '7. Ograniczenie odpowiedzialności',
          content: `W maksymalnym zakresie dozwolonym przez obowiązujące prawo:

• PenLab Peptide nie ponosi odpowiedzialności za jakiekolwiek szkody pośrednie, uboczne lub wynikowe
• Nasza łączna odpowiedzialność nie przekroczy kwoty zapłaconej za dany produkt
• Nie odpowiadamy za niewłaściwe użycie produktów ani nieprzestrzeganie protokołów badawczych
• Nie udzielamy żadnych gwarancji dotyczących przydatności do jakiegokolwiek szczególnego celu

Zobowiązujesz się zwolnić PenLab Peptide z odpowiedzialności i zabezpieczyć przed wszelkimi roszczeniami wynikającymi z Twojego korzystania z naszych produktów lub naruszenia niniejszych warunków.`
        }
      ],
      contact: 'Kontakt',
      contactText: 'Jeśli masz jakiekolwiek pytania dotyczące niniejszego Regulaminu, skontaktuj się z nami pod adresem:',
      email: 'info@penlabpeptide.com'
    },
    nl: {
      badge: "Juridisch",
      title: 'Algemene voorwaarden',
      lastUpdated: 'Laatst bijgewerkt',
      intro: 'Welkom bij PenLab Peptide. Door onze website en diensten te bezoeken en te gebruiken, gaat u akkoord met deze Algemene Voorwaarden. Lees deze zorgvuldig door voordat u een bestelling plaatst.',
      sections: [
        {
          icon: Scale,
          title: '1. Aanvaarding van de voorwaarden',
          content: `Door deze website te bezoeken, bevestigt u dat u een gediplomeerde professional, onderzoeker of gemachtigde vertegenwoordiger van een erkende instelling bent. U stemt ermee in alle toepasselijke wetten en voorschriften na te leven met betrekking tot de aankoop en het gebruik van onderzoeksstoffen. Als u niet akkoord gaat met deze voorwaarden, mag u onze diensten niet gebruiken.`
        },
        {
          icon: ShieldCheck,
          title: '2. Geschiktheid & verificatie',
          content: `Onze producten worden uitsluitend verkocht aan geverifieerde professionals en instellingen voor legitieme onderzoeksdoeleinden. Wij behouden ons het recht voor om:
          
• Verificatiedocumenten op te vragen, waaronder beroepslicenties, institutionele affiliatie en identificatie
• Bestellingen van niet-geverifieerde accounts te weigeren of te annuleren
• Accounts te beëindigen die onjuiste of misleidende informatie verstrekken
• Verdachte activiteiten te melden bij de bevoegde autoriteiten

Alle accountaanvragen zijn onderworpen aan ons verificatieproces, dat doorgaans 24-48 uur duurt.`
        },
        {
          icon: AlertTriangle,
          title: '3. Productgebruik, disclaimer',
          content: `BELANGRIJK: Alle producten die door PenLab Peptide worden verkocht, zijn uitsluitend bedoeld voor ONDERZOEKSDOELEINDEN. Onze producten zijn:

• NIET bedoeld voor menselijke of dierlijke consumptie
• NIET bedoeld voor diagnostisch, therapeutisch of medisch gebruik
• NIET goedgekeurd door enige regelgevende instantie voor klinisch gebruik
• Strikt verkocht voor in-vitroonderzoek en laboratoriumgebruik

Door onze producten te kopen, erkent en aanvaardt u dat u deze uitsluitend zult gebruiken voor legitiem wetenschappelijk onderzoek in overeenstemming met alle toepasselijke wetten en voorschriften.`
        },
        {
          icon: Package,
          title: '4. Bestellingen & verzending',
          content: `Verwerking van bestellingen:
• Alle bestellingen zijn onderhevig aan verificatie en aanvaarding
• Wij behouden ons het recht voor om naar eigen goeddunken elke bestelling te weigeren of te annuleren
• Orderbevestigingen vormen geen aanvaarding totdat de verificatie is voltooid

Verzending:
• Wij verzenden via DHL Express binnen 3-5 werkdagen naar de meeste Europese bestemmingen
• Levertijden kunnen variëren op basis van bestemming en douaneafhandeling
• Het risico op verlies gaat op u over bij aflevering aan de vervoerder
• Wij zijn niet verantwoordelijk voor vertragingen veroorzaakt door douane- of regelgevende controles

U bent verantwoordelijk voor het waarborgen van naleving van de invoervoorschriften in uw rechtsgebied.`
        },
        {
          icon: CreditCard,
          title: '5. Betalingsvoorwaarden',
          content: `Betaalmethoden:
• Wij accepteren uitsluitend betalingen via bankoverschrijving
• Alle bestellingen vereisen voltooiing van ons KYC-proces (Know Your Customer)
• Betaling moet zijn ontvangen voordat de orderverwerking begint

Prijzen:
• Alle prijzen worden weergegeven in euro (€) en zijn inclusief toepasselijke belastingen
• Wij behouden ons het recht voor om prijzen zonder voorafgaande kennisgeving te wijzigen
• Promotionele aanbiedingen kunnen onderworpen zijn aan aanvullende voorwaarden`
        },
        {
          icon: XCircle,
          title: '6. Retourneren & terugbetalingen',
          content: `Vanwege de aard van onze producten en wettelijke vereisten:

• Retourzendingen worden alleen geaccepteerd voor beschadigde of onjuiste artikelen
• Retouraanvragen moeten binnen 7 dagen na levering worden ingediend
• Producten moeten ongeopend zijn en in de originele verpakking zitten
• Restituties worden verwerkt binnen 14 werkdagen na ontvangst van de geretourneerde artikelen

Wij accepteren geen retourzendingen voor:
• Wijziging van inzicht of onjuiste bestelling
• Producten die zijn geopend of gemanipuleerd
• Bestellingen die zijn geweigerd door de douane in uw land`
        },
        {
          icon: RefreshCw,
          title: '7. Beperking van aansprakelijkheid',
          content: `Voor zover maximaal toegestaan door de wet:

• PenLab Peptide is niet aansprakelijk voor enige indirecte, incidentele of gevolgschade
• Onze totale aansprakelijkheid zal niet hoger zijn dan het bedrag dat is betaald voor het specifieke product in kwestie
• Wij zijn niet aansprakelijk voor enig misbruik van producten of het niet naleven van onderzoeksprotocollen
• Wij geven geen garanties omtrent geschiktheid voor enig bijzonder doel

U stemt ermee in PenLab Peptide te vrijwaren van en schadeloos te stellen voor alle aanspraken die voortvloeien uit uw gebruik van onze producten of schending van deze voorwaarden.`
        }
      ],
      contact: 'Contact',
      contactText: 'Als u vragen heeft over deze Algemene Voorwaarden, neem dan contact met ons op via:',
      email: 'info@penlabpeptide.com'
    },
    pt: {
      badge: "Jurídico",
      title: 'Termos e Condições',
      lastUpdated: 'Última atualização',
      intro: 'Bem-vindo à PenLab Peptide. Ao acessar e utilizar nosso site e serviços, você concorda em se vincular a estes Termos e Condições. Leia-os atentamente antes de fazer qualquer pedido.',
      sections: [
        {
          icon: Scale,
          title: '1. Aceitação dos Termos',
          content: `Ao acessar este site, você confirma que é um profissional licenciado, pesquisador ou representante autorizado de uma instituição licenciada. Você concorda em cumprir todas as leis e regulamentações aplicáveis relativas à compra e ao uso de compostos de pesquisa. Se você não concordar com estes termos, não deverá utilizar nossos serviços.`
        },
        {
          icon: ShieldCheck,
          title: '2. Elegibilidade e Verificação',
          content: `Nossos produtos são vendidos exclusivamente a profissionais e instituições verificados para fins legítimos de pesquisa. Reservamo-nos o direito de:
          
• Solicitar documentos de verificação, incluindo licenças profissionais, vínculo institucional e identificação
• Rejeitar ou cancelar pedidos de contas não verificadas
• Encerrar contas que forneçam informações falsas ou enganosas
• Comunicar atividades suspeitas às autoridades competentes

Todas as solicitações de conta estão sujeitas ao nosso processo de verificação, que normalmente leva de 24 a 48 horas.`
        },
        {
          icon: AlertTriangle,
          title: '3. Isenção de Responsabilidade sobre o Uso do Produto',
          content: `IMPORTANTE: Todos os produtos vendidos pela PenLab Peptide destinam-se EXCLUSIVAMENTE A FINS DE PESQUISA. Nossos produtos:

• NÃO se destinam ao consumo humano ou animal
• NÃO se destinam a uso diagnóstico, terapêutico ou medicinal
• NÃO são aprovados por nenhuma agência regulatória para uso clínico
• São vendidos estritamente para pesquisa in vitro e uso laboratorial

Ao adquirir nossos produtos, você reconhece e concorda que irá utilizá-los apenas para pesquisa científica legítima, em conformidade com todas as leis e regulamentações aplicáveis.`
        },
        {
          icon: Package,
          title: '4. Pedidos e Envio',
          content: `Processamento de pedidos:
• Todos os pedidos estão sujeitos à verificação e aceitação
• Reservamo-nos o direito de recusar ou cancelar qualquer pedido a nosso critério
• As confirmações de pedido não constituem aceitação até que a verificação seja concluída

Envio:
• Enviamos via DHL Express dentro de 3 a 5 dias úteis para a maioria dos destinos europeus
• Os prazos de envio podem variar de acordo com o destino e o processo aduaneiro
• O risco de perda é transferido para você no momento da entrega à transportadora
• Não nos responsabilizamos por atrasos causados por alfândega ou inspeções regulatórias

Você é responsável por garantir a conformidade com as regulamentações de importação em sua jurisdição.`
        },
        {
          icon: CreditCard,
          title: '5. Condições de Pagamento',
          content: `Métodos de pagamento:
• Aceitamos pagamentos apenas por transferência bancária
• Todos os pedidos exigem a conclusão do nosso processo de verificação KYC (Know Your Customer)
• O pagamento deve ser recebido antes do início do processamento do pedido

Preços:
• Todos os preços são exibidos em Euros (€) e incluem os impostos aplicáveis
• Reservamo-nos o direito de modificar os preços sem aviso prévio
• Ofertas promocionais podem estar sujeitas a termos e condições adicionais`
        },
        {
          icon: XCircle,
          title: '6. Devoluções e Reembolsos',
          content: `Devido à natureza de nossos produtos e às exigências regulatórias:

• Devoluções são aceitas apenas para itens danificados ou incorretos
• Os pedidos de devolução devem ser enviados em até 7 dias após a entrega
• Os produtos devem estar fechados e em sua embalagem original
• Os reembolsos serão processados em até 14 dias úteis após o recebimento dos itens devolvidos

Não aceitamos devoluções em caso de:
• Mudança de ideia ou pedido realizado de forma incorreta
• Produtos que tenham sido abertos ou adulterados
• Pedidos rejeitados pela alfândega em seu país`
        },
        {
          icon: RefreshCw,
          title: '7. Limitação de Responsabilidade',
          content: `Na máxima extensão permitida por lei:

• A PenLab Peptide não será responsável por quaisquer danos indiretos, incidentais ou consequenciais
• Nossa responsabilidade total não excederá o valor pago pelo produto específico em questão
• Não somos responsáveis por qualquer uso indevido dos produtos ou descumprimento de protocolos de pesquisa
• Não oferecemos garantias quanto à adequação para qualquer finalidade específica

Você concorda em isentar e defender a PenLab Peptide de quaisquer reivindicações decorrentes do uso de nossos produtos ou da violação destes termos.`
        }
      ],
      contact: 'Fale Conosco',
      contactText: 'Se você tiver alguma dúvida sobre estes Termos e Condições, entre em contato conosco em:',
      email: 'info@penlabpeptide.com'
    },
  };

  const t = translations[currentLanguage] || translations.en;

  const iconColors = [
    'from-capsule-teal to-capsule-aqua',
    'from-green-500 to-emerald-500',
    'from-capsule-coral to-capsule-salmon',
    'from-purple-500 to-violet-500',
    'from-amber-500 to-orange-500',
    'from-blue-500 to-cyan-500',
    'from-red-500 to-rose-500'
  ];

  return (
    <div className="min-h-screen bg-capsule-bg">
      <Header />

      {/* Hero Banner - Capsule 8 Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark">
        {/* DNA Decoration */}
        <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            <defs>
              <linearGradient id="termsGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#termsGradient)" strokeWidth="3" fill="none"/>
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#termsGradient)" strokeWidth="3" fill="none"/>
          </svg>
        </div>
        
        {/* Floating spheres */}
        <div className="absolute top-20 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl"></div>
        <div className="absolute bottom-20 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="text-center max-w-3xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/20">
              <FileText className="w-4 h-4 text-capsule-aqua" />
              <span className="text-sm font-medium text-capsule-aqua">{t.badge}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {t.title}
            </h1>
            <p className="text-lg text-gray-300">
              {t.lastUpdated}: December 2025
            </p>
          </div>
        </div>
        
        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 37 768 43 864 45C960 47 1056 45 1152 41.7C1248 38 1344 33 1392 30.8L1440 29V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0Z" fill="#E7EAED"/>
          </svg>
        </div>
      </div>
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Introduction */}
        <div className="glass-card rounded-3xl p-8 mb-8">
          <p className="text-gray-700 leading-relaxed text-lg">{t.intro}</p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {t.sections.map((section, index) => {
            const IconComponent = section.icon;
            return (
              <div key={index} className="glass-card rounded-3xl p-8 hover:shadow-glass-hover transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 w-12 h-12 bg-gradient-to-br ${iconColors[index]} rounded-xl flex items-center justify-center shadow-lg`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">{section.title}</h2>
                    <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {section.content}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-r from-capsule-teal to-capsule-aqua rounded-3xl shadow-teal-glow p-8 mt-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-4">{t.contact}</h2>
          <p className="mb-6 opacity-90">{t.contactText}</p>
          <a 
            href={`mailto:${t.email}`}
            className="inline-flex items-center gap-2 bg-white text-capsule-teal px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors shadow-lg"
          >
            <Mail className="w-5 h-5" />
            {t.email}
          </a>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsPage;
